/**
 * Batch Executor
 *
 * Orchestrates submitting generation jobs with:
 * - Configurable concurrency (1-4 parallel jobs)
 * - Per-item tracking (BatchItem)
 * - Retry logic with backoff
 * - Pause/cancel signals
 * - Cost extraction from job results
 */

import { useJobStore } from '@/stores/jobStore'

// ── Types ───────────────────────────────────────────────────────────────────

export interface BatchJobParams {
  nodeId: string
  nodeType: 'image' | 'video' | 'text'
  purpose?: string
  subPurpose?: string
  sourceFamily?: string
  prompt: string
  modelId: string
  settings?: Record<string, unknown>
  variantCount?: number
}

export interface BatchItem {
  index: number
  promptIndex: number
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'skipped'
  jobId?: string
  outputNodeId?: string
  outputUrl?: string
  error?: string
  retryCount: number
  startedAt?: string
  completedAt?: string
  durationMs?: number
  creditsCost?: number
}

export interface BatchExecutorParams {
  items: Array<{
    index: number
    prompt: string
    negativePrompt?: string
  }>
  jobTemplate: {
    nodeId: string
    nodeType: 'image' | 'video' | 'text'
    purpose?: string
    subPurpose?: string
    sourceFamily?: string
    modelId: string
    settings?: Record<string, unknown>
  }
  submitJob: (params: BatchJobParams) => Promise<string | null>
  onItemStart: (index: number) => void
  onItemComplete: (index: number, result: { jobId: string; outputUrl?: string; fileUuid?: string; creditsCost?: number }) => void
  onItemError: (index: number, error: string, retryCount: number) => void
  onProgress: (completed: number, total: number) => void
  onBatchItemUpdate: (index: number, item: Partial<BatchItem>) => void
  shouldPause: () => boolean
  shouldCancel: () => boolean
  pauseOnError?: boolean
  maxRetries?: number
  concurrency?: number
  skipCompleted?: BatchItem[]
}

export interface BatchResult {
  completed: number
  failed: number
  cancelled: boolean
  items: BatchItem[]
}

// ── Wait for job completion ─────────────────────────────────────────────────

function waitForJobCompletion(jobId: string): Promise<{ status: 'COMPLETED' | 'FAILED'; output?: unknown; error?: string }> {
  return new Promise((resolve) => {
    const current = useJobStore.getState().jobs.get(jobId)
    if (current?.status === 'COMPLETED') {
      resolve({ status: 'COMPLETED', output: current.output })
      return
    }
    if (current?.status === 'FAILED') {
      resolve({ status: 'FAILED', error: current.error })
      return
    }

    const unsub = useJobStore.subscribe((state) => {
      const job = state.jobs.get(jobId)
      if (!job) return
      if (job.status === 'COMPLETED') {
        unsub()
        resolve({ status: 'COMPLETED', output: job.output })
      } else if (job.status === 'FAILED') {
        unsub()
        resolve({ status: 'FAILED', error: job.error })
      }
    })
  })
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function extractOutputUrl(output: unknown): string | undefined {
  if (!output || typeof output !== 'object') return undefined
  const o = output as Record<string, unknown>

  if (Array.isArray(o.processedFiles) && o.processedFiles.length > 0) {
    const file = o.processedFiles[0] as Record<string, unknown>
    return file.publicUrl as string | undefined
  }

  if (Array.isArray(o.variants) && o.variants.length > 0) {
    const v = o.variants[0] as Record<string, unknown>
    return (v.imageUrl ?? v.videoUrl) as string | undefined
  }

  return undefined
}

export function extractFileUuid(output: unknown): string | undefined {
  if (!output || typeof output !== 'object') return undefined
  const o = output as Record<string, unknown>

  if (Array.isArray(o.processedFiles) && o.processedFiles.length > 0) {
    const file = o.processedFiles[0] as Record<string, unknown>
    return (file.uuid ?? file.fileUuid) as string | undefined
  }

  if (Array.isArray(o.variants) && o.variants.length > 0) {
    const v = o.variants[0] as Record<string, unknown>
    return (v.uuid ?? v.fileUuid) as string | undefined
  }

  // Direct uuid on output
  if (typeof o.uuid === 'string') return o.uuid
  if (typeof o.fileUuid === 'string') return o.fileUuid

  return undefined
}

export function extractCreditsCost(output: unknown): number | undefined {
  if (!output || typeof output !== 'object') return undefined
  const o = output as Record<string, unknown>
  if (typeof o.creditsCost === 'number') return o.creditsCost
  if (typeof o.creditsCharged === 'number') return o.creditsCharged
  return undefined
}

// ── Process single item ─────────────────────────────────────────────────────

async function processItem(
  item: { index: number; prompt: string; negativePrompt?: string },
  jobTemplate: BatchExecutorParams['jobTemplate'],
  submitJob: BatchExecutorParams['submitJob'],
  maxRetries: number,
  onItemStart: BatchExecutorParams['onItemStart'],
  onItemComplete: BatchExecutorParams['onItemComplete'],
  onItemError: BatchExecutorParams['onItemError'],
  onBatchItemUpdate: BatchExecutorParams['onBatchItemUpdate'],
): Promise<{ success: boolean; creditsCost?: number }> {
  onItemStart(item.index)
  onBatchItemUpdate(item.index, {
    status: 'running',
    startedAt: new Date().toISOString(),
  })

  const startTime = Date.now()
  let retryCount = 0

  while (retryCount <= maxRetries) {
    try {
      const jobId = await submitJob({
        ...jobTemplate,
        prompt: item.prompt,
      })

      if (!jobId) {
        throw new Error('Failed to submit job')
      }

      onBatchItemUpdate(item.index, { jobId })

      const result = await waitForJobCompletion(jobId)

      if (result.status === 'COMPLETED') {
        const outputUrl = extractOutputUrl(result.output)
        const fileUuid = extractFileUuid(result.output)
        const creditsCost = extractCreditsCost(result.output)
        const durationMs = Date.now() - startTime

        onBatchItemUpdate(item.index, {
          status: 'succeeded',
          outputUrl,
          creditsCost,
          completedAt: new Date().toISOString(),
          durationMs,
          retryCount,
        })
        onItemComplete(item.index, { jobId, outputUrl, fileUuid, creditsCost })
        return { success: true, creditsCost }
      } else {
        retryCount++
        if (retryCount > maxRetries) {
          const durationMs = Date.now() - startTime
          onBatchItemUpdate(item.index, {
            status: 'failed',
            error: result.error || 'Generation failed',
            completedAt: new Date().toISOString(),
            durationMs,
            retryCount,
          })
          onItemError(item.index, result.error || 'Generation failed', retryCount)
          return { success: false }
        }
        await sleep(1000 * retryCount)
      }
    } catch (err) {
      retryCount++
      if (retryCount > maxRetries) {
        const durationMs = Date.now() - startTime
        const errMsg = err instanceof Error ? err.message : 'Unknown error'
        onBatchItemUpdate(item.index, {
          status: 'failed',
          error: errMsg,
          completedAt: new Date().toISOString(),
          durationMs,
          retryCount,
        })
        onItemError(item.index, errMsg, retryCount)
        return { success: false }
      }
      await sleep(1000 * retryCount)
    }
  }

  return { success: false }
}

// ── Main executor with concurrency ──────────────────────────────────────────

export async function executeBatch({
  items,
  jobTemplate,
  submitJob,
  onItemStart,
  onItemComplete,
  onItemError,
  onProgress,
  onBatchItemUpdate,
  shouldPause,
  shouldCancel,
  pauseOnError = false,
  maxRetries = 3,
  concurrency = 1,
  skipCompleted,
}: BatchExecutorParams): Promise<BatchResult> {
  let completed = 0
  let failed = 0
  let cancelled = false

  // Filter out already-completed items if skipCompleted provided
  const itemsToProcess = skipCompleted
    ? items.filter((item) => {
        const existing = skipCompleted.find((bi) => bi.index === item.index)
        if (existing?.status === 'succeeded') {
          onBatchItemUpdate(item.index, { status: 'skipped' })
          return false
        }
        return true
      })
    : items

  const batchItems: BatchItem[] = items.map((item) => ({
    index: item.index,
    promptIndex: item.index,
    status: 'queued' as const,
    retryCount: 0,
  }))

  const effectiveConcurrency = Math.max(1, Math.min(4, concurrency))

  if (effectiveConcurrency === 1) {
    // Sequential execution
    for (let i = 0; i < itemsToProcess.length; i++) {
      if (shouldCancel()) { cancelled = true; break }

      while (shouldPause() && !shouldCancel()) {
        await sleep(500)
      }
      if (shouldCancel()) { cancelled = true; break }

      const result = await processItem(
        itemsToProcess[i], jobTemplate, submitJob, maxRetries,
        onItemStart, onItemComplete, onItemError, onBatchItemUpdate,
      )

      if (result.success) {
        completed++
      } else {
        failed++
        if (pauseOnError) {
          // Signal pause — the UI will show paused state
          // Wait until unpaused or cancelled
          while (shouldPause() && !shouldCancel()) {
            await sleep(500)
          }
          if (shouldCancel()) { cancelled = true; break }
        }
      }

      onProgress(completed + failed, items.length)
    }
  } else {
    // Concurrent execution using a worker pool
    let nextIdx = 0
    const activeWorkers: Promise<void>[] = []

    const spawnWorker = async () => {
      while (nextIdx < itemsToProcess.length) {
        if (shouldCancel()) { cancelled = true; return }

        while (shouldPause() && !shouldCancel()) {
          await sleep(500)
        }
        if (shouldCancel()) { cancelled = true; return }

        const idx = nextIdx++
        if (idx >= itemsToProcess.length) return

        const result = await processItem(
          itemsToProcess[idx], jobTemplate, submitJob, maxRetries,
          onItemStart, onItemComplete, onItemError, onBatchItemUpdate,
        )

        if (result.success) {
          completed++
        } else {
          failed++
          if (pauseOnError) {
            while (shouldPause() && !shouldCancel()) {
              await sleep(500)
            }
            if (shouldCancel()) { cancelled = true; return }
          }
        }

        onProgress(completed + failed, items.length)
      }
    }

    for (let w = 0; w < effectiveConcurrency; w++) {
      activeWorkers.push(spawnWorker())
    }

    await Promise.all(activeWorkers)
  }

  return { completed, failed, cancelled, items: batchItems }
}
